/**
 * QR App - © 2025 Juan Pablo Marín Del Rincón. Todos los derechos reservados.
 *
 * Este sistema fue desarrollado exclusivamente para Dinámica Juvenil Sfa.
 * Se prohíbe su venta, redistribución, modificación o uso no autorizado,
 * total o parcial, sin consentimiento por escrito del autor.
 *
 * Protegido por la Ley Federal del Derecho de Autor (LFDA), Título VII:
 * - Capítulo I: Infracciones Administrativas
 * - Capítulo II: Delitos
 * - Capítulo III: Sanciones
 *
 * Cualquier uso indebido podrá ser sancionado conforme a la ley.
 * Contacto: jpmarin1999.@gmail.com
 */

// ROLLBACK 15/02/2026
// Motivo: incompatibilidad de la librería html5-qrcode con versiones antiguas de Android e iOS y móviles con pantalla pequeña.
// Se implementará una solución personalizada de escaneo QR en el futuro para garantizar compatibilidad total.
// Mientras tanto, se mantiene esta versión para asegurar que todos los usuarios puedan seguir utilizando la función de escaneo sin problemas.

import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { message, theme } from "antd";
import { Html5Qrcode } from "html5-qrcode";
import { getBackendUrl } from "../../utils/env";
import axios from "axios";

/* ─── ESTILOS DINÁMICOS ───────────────────────────────────────────────────── */
const makeStyles = (t) => `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=Mulish:wght@400;500;600&display=swap');

  .qr-root {
    font-family: 'Mulish', sans-serif;
    background: ${t.colorBgLayout};
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: clamp(24px, 5vw, 48px) clamp(16px, 4vw, 32px);
  }

  /* ── Tarjeta contenedora ── */
  .qr-card {
    width: 100%;
    max-width: 420px;
    background: ${t.colorBgContainer};
    border: 1px solid ${t.colorBorderSecondary};
    border-radius: 28px;
    overflow: hidden;
    box-shadow: ${t.boxShadowSecondary};
    animation: fadeUp 0.45s ease both;
  }

  /* ── Cabecera ── */
  .qr-card-head {
    padding: 24px 28px 20px;
    border-bottom: 1px solid ${t.colorBorderSecondary};
    text-align: center;
  }
  .qr-icon-wrap {
    width: 48px;
    height: 48px;
    background:  #ffe000;
    border: 1px solid ${t.colorPrimaryBorder};
    border-radius: 14px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 22px;
    margin-bottom: 12px;
  }
  .qr-title {
    font-family: 'Syne', sans-serif;
    font-size: clamp(20px, 5vw, 26px);
    font-weight: 800;
    color: ${t.colorText};
    margin: 0 0 4px;
    line-height: 1.15;
  }
  .qr-subtitle {
    font-size: 13px;
    color: ${t.colorTextSecondary};
    margin: 0;
  }

  /* ── Visor de cámara ── */
  .qr-viewport-wrap {
    position: relative;
    padding: 20px 20px 0;
  }
  .qr-viewport {
    position: relative;
    width: 100%;
    aspect-ratio: 1 / 1;
    border-radius: 18px;
    overflow: hidden;
    background: #000;
  }

  /* html5-qrcode inyecta un <video> aquí — lo forzamos a llenar */
  .qr-viewport video {
    width: 100% !important;
    height: 100% !important;
    object-fit: cover !important;
  }
  .qr-viewport img { display: none !important; } /* oculta el ícono de QR de la librería */

  /* Marco de esquinas animado */
  .qr-frame {
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: 10;
  }
  .qr-corner {
    position: absolute;
    width: 28px;
    height: 28px;
    border-color:  #ffe000;
    border-style: solid;
    border-width: 0;
  }
  .qr-corner--tl { top: 16px;    left: 16px;    border-top-width: 3px;    border-left-width: 3px;    border-radius: 4px 0 0 0; }
  .qr-corner--tr { top: 16px;    right: 16px;   border-top-width: 3px;    border-right-width: 3px;   border-radius: 0 4px 0 0; }
  .qr-corner--bl { bottom: 16px; left: 16px;    border-bottom-width: 3px; border-left-width: 3px;    border-radius: 0 0 0 4px; }
  .qr-corner--br { bottom: 16px; right: 16px;   border-bottom-width: 3px; border-right-width: 3px;   border-radius: 0 0 4px 0; }

  /* Línea de escaneo */
  .qr-scanline {
    position: absolute;
    left: 16px;
    right: 16px;
    height: 2px;
    background: linear-gradient(90deg, transparent, #ffe000, transparent);
    border-radius: 2px;
    animation: scanMove 2s ease-in-out infinite;
    z-index: 11;
    opacity: 0.8;
  }
  @keyframes scanMove {
    0%   { top: 20px;  opacity: 0; }
    10%  { opacity: 1; }
    90%  { opacity: 1; }
    100% { top: calc(100% - 20px); opacity: 0; }
  }

  /* Overlay de procesando */
  .qr-processing-overlay {
    position: absolute;
    inset: 0;
    background: rgba(0,0,0,0.65);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    z-index: 20;
    border-radius: 18px;
    animation: fadeIn 0.2s ease;
  }
  .qr-spinner {
    width: 44px;
    height: 44px;
    border: 3px solid rgba(255,255,255,0.15);
    border-top-color:  #ffe000;
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
  }
  .qr-processing-text {
    font-family: 'Syne', sans-serif;
    font-size: 14px;
    font-weight: 700;
    color: #fff;
    letter-spacing: 0.5px;
  }
  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  /* ── Estado (badge de estado) ── */
  .qr-footer {
    padding: 16px 20px 22px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 14px;
  }
  .qr-status {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 500;
    color: ${t.colorTextSecondary};
  }
  .qr-status-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
  }
  .qr-status-dot--active {
    background: ${t.colorSuccess};
    box-shadow: 0 0 0 3px ${t.colorSuccessBg};
    animation: pulse 1.8s ease-in-out infinite;
  }
  .qr-status-dot--idle {
    background: ${t.colorTextTertiary};
  }
  @keyframes pulse {
    0%, 100% { box-shadow: 0 0 0 2px ${t.colorSuccessBg}; }
    50%       { box-shadow: 0 0 0 5px ${t.colorSuccessBg}; }
  }

  /* ── Botón detener ── */
  .qr-stop-btn {
    width: 100%;
    height: 46px;
    border-radius: 12px;
    border: 1.5px solid ${t.colorErrorBorder};
    background: ${t.colorErrorBg};
    color: ${t.colorError};
    font-family: 'Syne', sans-serif;
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 0.5px;
    cursor: pointer;
    transition: all 0.15s;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }
  .qr-stop-btn:hover:not(:disabled) {
    background: ${t.colorError};
    color: #fff;
    border-color: ${t.colorError};
  }
  .qr-stop-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  /* ── Hint ── */
  .qr-hint {
    font-size: 12px;
    color: ${t.colorTextTertiary};
    text-align: center;
    line-height: 1.5;
    padding: 0 8px;
  }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(16px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes fadeIn {
    from { opacity: 0; } to { opacity: 1; }
  }
`;

/* ─── COMPONENTE ──────────────────────────────────────────────────────────── */
export default function QRScanner() {
  const { token }  = theme.useToken();
  const location   = useLocation();
  const css        = makeStyles(token);

  const [loading,        setLoading]        = useState(false);
  const [scannerActivo,  setScannerActivo]  = useState(false);

  const html5QrCodeRef = useRef(null);
  const isMounted      = useRef(true);
  const scannedCodes   = useRef(new Set());

  /* ── Detener escáner (sin cambios de lógica) ── */
  const stopScannerSafe = async () => {
    if (html5QrCodeRef.current) {
      const state = html5QrCodeRef.current.getState();
      if (state === 1 || state === 2) {
        try {
          await html5QrCodeRef.current.stop();
          await html5QrCodeRef.current.clear();
          setScannerActivo(false);
        } catch (err) {
          console.warn("Error al detener el escáner:", err);
        }
      }
    }
  };

  /* ── Limpieza al desmontar / cambio de ruta ── */
  useEffect(() => {
    return () => {
      isMounted.current = false;
      stopScannerSafe();
      navigator.mediaDevices
        .getUserMedia({ video: true })
        .then((stream) => stream.getTracks().forEach((t) => t.stop()))
        .catch(() => {});
    };
  }, [location.pathname]);

  /* ── Iniciar escáner ── */
  useEffect(() => {
    const user_id = localStorage.getItem("user_id");
    if (!user_id) { message.error("Usuario no autenticado"); return; }

    if (!html5QrCodeRef.current) {
      html5QrCodeRef.current = new Html5Qrcode("qr-reader");
    }

    const onSuccess = async (decodedText) => {
      if (loading) return;
      if (scannedCodes.current.has(decodedText)) return;

      setLoading(true);
      scannedCodes.current.add(decodedText);

      try {
        const res = await axios.post(
          `${getBackendUrl()}api/scan_qr.php`,
          { user_id, code: decodedText },
          { headers: { "Content-Type": "application/json" } }
        );
        const data = res.data;
        if (data.success) message.success(data.message);
        else              message.warning(data.message);
      } catch (error) {
        message.error("Error al escanear: " + (error.response?.data?.message || error.message));
      } finally {
        setLoading(false);
      }
    };

    const startScanner = async () => {
      if (!html5QrCodeRef.current) return;
      try {
        await html5QrCodeRef.current.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: 250 },
          onSuccess,
          () => {} // error silencioso mientras espera QR
        );
        setScannerActivo(true);
      } catch (err) {
        console.error("No se pudo iniciar la cámara:", err);
        message.warning("Permite el acceso a la cámara para escanear.");
      }
    };

    startScanner();

    return () => {
      isMounted.current = false;
      stopScannerSafe();
      navigator.mediaDevices
        .getUserMedia({ video: true })
        .then((stream) => stream.getTracks().forEach((t) => t.stop()))
        .catch(() => {});
    };
  }, []);

  const detenerManual = async () => {
    await stopScannerSafe();
    message.info("Escáner detenido");
  };

  /* ── Render ── */
  return (
    <div className="qr-root">
      <style>{css}</style>

      <div className="qr-card">

        {/* Cabecera */}
        <div className="qr-card-head">
          <div className="qr-icon-wrap">▦</div>
          <h1 className="qr-title">Escanear QR</h1>
          <p className="qr-subtitle">Apunta la cámara al código para registrar tu actividad</p>
        </div>

        {/* Visor */}
        <div className="qr-viewport-wrap">
          <div className="qr-viewport">
            {/* html5-qrcode renderiza el video aquí */}
            <div id="qr-reader" style={{ width: "100%", height: "100%" }} />

            {/* Marco de esquinas */}
            <div className="qr-frame">
              <div className="qr-corner qr-corner--tl" />
              <div className="qr-corner qr-corner--tr" />
              <div className="qr-corner qr-corner--bl" />
              <div className="qr-corner qr-corner--br" />
              {scannerActivo && !loading && <div className="qr-scanline" />}
            </div>

            {/* Overlay procesando */}
            {loading && (
              <div className="qr-processing-overlay">
                <div className="qr-spinner" />
                <span className="qr-processing-text">Procesando…</span>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="qr-footer">

          {/* Estado */}
          <div className="qr-status">
            <div className={`qr-status-dot ${scannerActivo ? "qr-status-dot--active" : "qr-status-dot--idle"}`} />
            {loading
              ? "Registrando asistencia…"
              : scannerActivo
              ? "Cámara activa · buscando código"
              : "Cámara inactiva"}
          </div>

          {/* Botón */}
          <button
            className="qr-stop-btn"
            onClick={detenerManual}
            disabled={!scannerActivo}
          >
            ⬛ Detener escáner
          </button>

          {/* Hint */}
          <p className="qr-hint">
            Los códigos ya escaneados en esta sesión no se registrarán dos veces.
          </p>
        </div>
      </div>
    </div>
  );
}
